import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress, 
  useMediaQuery,
  Divider
} from '@mui/material';
import { SentimentSatisfied } from '@mui/icons-material';

import DepartmentCard from '../cards/DepartmentCard';
import EmpStatCard from '../cards/EmpStatCard';
import EmployeeAbsenceTable from '../tables/EmployeeAttendance';
import AbsenceAnalyticsTables from '../tables/AbsenceAnalyticsTables';
import LatestAbsences from '../lists/LatestAbsences';
import DashboardFilters from '../controls/OfficeMonthYear';
import { useTheme } from '@mui/material/styles';

import {getAllEmployeesAbsenceSummaryByEmployeeAndType, getAllEmployeesAbsenceCountByType, 
  getAllEmployeesAbsenceSummary, getLatestAbsences, getAbsencesByDepartment,
getHighAbsenceEmployees, getEmployeeAbsenceSummaries} from '../../services/absenceAPIs';


const AbsenceDashboard = ({IMGemployee,
  sick=false,
  late=false,
  perso=false
}) => {
  const isLargeScreen = useMediaQuery('(min-width: 1600px)');

  const theme = useTheme();

  const [absenceSummary, setAbsenceSummary] = useState({});
  const [latestAbsences, setLatestAbsences] = useState([]);
  const [absencesByDepartment, setAbsencesByDepartment] = useState({});
  const [employee, setEmployee] = useState([]);
  const [loading, setLoading] = useState(true);

  // Controlls
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [periodType, setPeriodType] = useState("year");
  const [periodValue, setPeriodValue] = useState(new Date().getFullYear().toString());
  const [office, setOffice] = useState("All");

  const getPeriodOptions = () => {
    if (periodType === "year") {
      return [
        { value: "2024", label: "2024" },
        { value: "2025", label: "2025" },
        { value: "2026", label: "2026" }
      ];
    }
    if (periodType === "month") {
      return [
        { value: "2025-01", label: "January" },
        { value: "2025-02", label: "February" },
        { value: "2025-03", label: "March" },
        { value: "2025-04", label: "April" },
        { value: "2025-05", label: "May" },
        { value: "2025-06", label: "June" },
        { value: "2025-07", label: "July" },
        { value: "2025-08", label: "August" },
        { value: "2025-09", label: "September" },
        { value: "2025-10", label: "October" },
        { value: "2025-11", label: "November" },
        { value: "2025-12", label: "December" }
      ];
    }
    return [];
  };

  // CSS-in-JS styles
  const styles = {
    dashboard: {
      minWidth:'100%',
      minHeight: '100%',
      fontFamily: 'Roboto, sans-serif'
    },
    headerCard: {
      background:'transparent',
      padding:0,
      margin:0,
    },
  };

  useEffect(() => {
  // const loadData = async () => {
  //   setLoading(true);

  //   // Clear old data so it doesn't flash
  //   setAbsenceSummary({});
  //   setLatestAbsences([]);
  //   setAbsencesByDepartment({});
  //   setEmployee([]);

  //   try {
  //     const year = periodType === "year" ? periodValue : periodValue.split('-')[0];
  //     const month = periodType === "month" ? periodValue.split('-')[1] : null;

  //     const summary = await getAllEmployeesAbsenceCountByType(year, month, office);
  //     const latest = await getLatestAbsences(year, month, office);
  //     const byDept = await getAbsencesByDepartment(year, month, office);
  //     const employeeSummary = await getAllEmployeesAbsenceSummaryByEmployeeAndType(year, month, office);
  //     console.log('Employee Summary:', employeeSummary);
  //     // Transform employee data
  //     const transformedEmployees = Object.entries(employeeSummary).map(([empKey, empData]) => {
  //       const [firstName, lastName] = empKey.split(' ');
  //       return {
  //         firstName: firstName || empKey,
  //         lastName: lastName || '',
  //         ...empData,
  //       };
  //     });
  //     console.log('employees', transformedEmployees);

  //     setAbsenceSummary(summary);
  //     setLatestAbsences(latest);
  //     setAbsencesByDepartment(byDept);
  //     setEmployee(transformedEmployees);
  //   } catch (error) {
  //     console.error('Error loading absence data:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
      setLoading(false);
  // loadData();
  }, [periodType, periodValue, office]);

  const filterEmployeesByType = (type) => {
    let filteredEmployees = [];
    if(employee.length > 0){
      filteredEmployees = employee.filter(emp => emp.leaveType === type);
      console.log('Filtered Employees:', filteredEmployees);
    }
  
    return filteredEmployees;
  };

  const getLeaveTypeColor = (type) => {
    const colors = {
      'LATENESS': ' #f39c12',       
      'PARTIAL_DAY': '#ff8c7fff',    
      // 'FULL_DAY': '#3498db',
      'FULL_DAY': '#ff5744ff',
      // 'MULTIPLE_DAYS': '#9b59b6',
      'MULTIPLE_DAYS': '#ff1900ff',    
      // 'LATENESS': '#ff6b6b',
      // 'VACATION': '#4ecdc4',
      // 'PERSONAL_LEAVE': '#45b7d1',
      // 'MATERNITY_LEAVE': '#f9ca24',
      'EMERGENCY_LEAVE': '#f0932b'
    };
    return colors[type] || '#6c757d';
  };

  const getLeaveTypeIcon = (type) => {
    const icons = {
      'SICK_LEAVE': '🏥',
      'VACATION': '🏖️',
      'PERSONAL_LEAVE': '🏠',
      'MATERNITY_LEAVE': '👶',
      'EMERGENCY_LEAVE': '🚨',
      'LATENESS': '⏰',
      'PARTIAL_DAY': '🕒',
      'FULL_DAY': '📅',
      'MULTIPLE_DAYS': '📆'
    };
    return icons[type] || '📅';
  };

  const formatLeaveType = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const getTotalDays = () => {
    return Object.values(absenceSummary).reduce((sum, days) => sum + days, 0);
  };

  const getDepartmentStats = (dept, absences) => {
    const totalDays = absences.reduce((sum, absence) => sum + absence.days, 0);
    const totalEmployees = new Set(absences.map(a => a.employee.firstName + a.employee.lastName)).size;
    return { totalDays, totalEmployees };
  };

  

  if (loading) {
    return (
      <Box sx={styles.dashboard}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          {/* <LinearProgress sx={{ width: '300px', height: '6px', borderRadius: '3px' }} /> */}
           <CircularProgress size={60} thickness={4} sx={{ color: '#3d52a0' }} />
        </Box>
      </Box>
    );
  }
  

  return (
    <Box sx={styles.dashboard}>
      {/* <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#764ba2', mb: 1, alignSelf:'start' }}>
        Employees Rate
      </Typography> */}
      {/* <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#764ba2', mb: 1, alignSelf:'start' }}>
        Employees Absence Rate
      </Typography>
      <AbsenceAnalyticsTables /> */}
      
      {/* <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#764ba2', mb: 1, alignSelf:'start' }}>
        Employees{' '}
          <Box component="span" sx={{ backgroundColor: 'yellow', px: 0.5 }}>
            Lateness
          </Box>{' '} 
          Rate
      </Typography> */}
      {late &&
      <>
        <AbsenceAnalyticsTables lateness={true} />
      </>
      }
      {sick &&
        <>
          <AbsenceAnalyticsTables sickness={true} />
        </>
      }
      {perso &&
        <>
          <AbsenceAnalyticsTables personal={true} />
        </>
      }
    
      {/* Header */}
      {/* <Paper sx={styles.headerCard}>
        <CardContent sx={{ paddingLeft: 0, paddingRight: 0, mb:0, mt:0,  paddingTop: 0, paddingBottom: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding:0, margin:0 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#764ba2', mb: 1, alignSelf:'start' }}>
                Absence Analytics Dashboard
              </Typography>
              <Typography variant="body1" sx={{ color: '#667eea' }}>
                Real-time insights into employee absence patterns
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                icon={<TrendingUp />} 
                label={`${getTotalDays()} Total Days`} 
                sx={{ 
                  background: 'linear-gradient(45deg, #667eea, #764ba2)', 
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
              <IconButton 
                onClick={() => window.location.reload()}
                sx={{ 
                  background: 'linear-gradient(45deg, #667eea, #764ba2)', 
                  color: 'white',
                  '&:hover': { transform: 'rotate(180deg)' }
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Paper> */}
        {/* <DashboardFilters
          filtersOpen={filtersOpen}
          setFiltersOpen={setFiltersOpen}
          periodType={periodType}
          setPeriodType={setPeriodType}
          periodValue={periodValue}
          setPeriodValue={setPeriodValue}
          office={office}
          setOffice={setOffice}
          getPeriodOptions={getPeriodOptions}
        /> */}
        
       {/* Absence Summary Cards */}
         {/*<Box sx={{width:'100%'}} >
          <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
            📊 Absence Summary by Type
          </Typography>
          <Box sx={{ width: '100%', mt: 3 }}>
            <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
              📋 Employee Absence Records
            </Typography>
          <EmployeeAbsenceTable
            employees={employee}
            getLeaveTypeColor={getLeaveTypeColor}
            getLeaveTypeIcon={getLeaveTypeIcon}
            formatLeaveType={formatLeaveType}
            IMGemployees={IMGemployees}
          />
         </Box>
           {isLargeScreen ? (
            // Large screen: all in one row
            <Box sx={{ display: 'flex', gap: 3, flexDirection: 'row', width: '99.8%' }}>
              {Object.entries(absenceSummary).map(([type, { days, count }]) => (
                <Box key={type} sx={{ width: '80%' }}>
                  <EmpStatCard
                    type={type}
                    days={days}
                    count={count}
                    totalDays={getTotalDays()}
                    getLeaveTypeColor={getLeaveTypeColor}
                    getLeaveTypeIcon={getLeaveTypeIcon}
                    formatLeaveType={formatLeaveType}
                    employees={filterEmployeesByType(type)}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            // Small screen: two per row
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%' }}>
              {Object.entries(absenceSummary).map(([type, { days, count }]) => (
                <Box key={type}>
                  <EmpStatCard
                    type={type}
                    days={days}
                    count={count}
                    totalDays={getTotalDays()}
                    getLeaveTypeColor={getLeaveTypeColor}
                    getLeaveTypeIcon={getLeaveTypeIcon}
                    formatLeaveType={formatLeaveType}
                    employees={filterEmployeesByType(type)}
                   />
                </Box>
              ))}
            </Box>
          )} 

          </Box>*/}

      <Box sx={{display: 'flex',gap: 2, width:'100%', mt:1 }}>

      
        {/* Absences by Department */}
         {/* <Box sx={{ mt: 4, width:'100%' }}>
            <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
              🏢 Absences by Department
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(absencesByDepartment).length == 0 ? (
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 4,
                  textAlign: 'center',
                  width: '100%',
                  height:'100%',
                  boxShadow: theme.shadows[6], 
                  borderRadius: '16px',
                  border: '1px solid rgba(103, 58, 183, 0.2)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)'
                  }
                }}>
                  <Box sx={{ 
                    // background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: '-10px',
                      left: '-10px',
                      right: '-10px',
                      bottom: '-10px',
                      borderRadius: '50%',
                      border: '2px solid rgba(76, 175, 80, 0.3)',
                      animation: 'ripple 2s infinite'
                    }
                  }}>
                    <SentimentSatisfied sx={{ color: '#4caf50', fontSize: '30px' }} />
                  </Box>
                  <Typography variant="h6" sx={{ 
                    color: '#4caf50', 
                    fontWeight: 'bold',
                    mb: 1 
                  }}>
                    All Good! 🎉
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#2e7d32',
                    fontStyle: 'italic'
                  }}>
                    No department absences recorded
                  </Typography>
                  <Box sx={{ 
                    mt: 2, 
                    px: 2, 
                    py: 1, 
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '16px',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}>
                  <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                    All teams fully staffed and ready 🚀
                  </Typography>
                </Box>
              </Box>
              ) : (
                <>
              {Object.entries(absencesByDepartment).map(([dept, absences], index) => (
                // <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ width: isLargeScreen ? '48%' : '99%' }} key={index}>
                  <DepartmentCard
                    department={dept}
                    absences={absences}
                    getDepartmentStats={getDepartmentStats}
                    formatLeaveType={formatLeaveType}
                    getLeaveTypeColor={getLeaveTypeColor}
                  />
                </Box>
              ))}
              </>
              )}
            </Grid>
          </Box> */}
          {/* Latest Absences */}
        {/* {isLargeScreen ? (
        <Box sx={{width:'50%', mt: 4 }}>
           <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
            🕐 Latest Absences
          </Typography> 
          <LatestAbsences
            latestAbsences={latestAbsences}
            getLeaveTypeColor={getLeaveTypeColor}
            formatLeaveType={formatLeaveType}
          />
        </Box>
        ):(
          <Box sx={{width:'99%', mt: 4 }}>
           <Typography variant="h6" sx={{ color: '#3d52a0', mb: 2, fontWeight: 'bold' }}>
            🕐 Latest Absences
          </Typography>
          <LatestAbsences
            latestAbsences={latestAbsences}
            getLeaveTypeColor={getLeaveTypeColor}
            formatLeaveType={formatLeaveType}
          />
        </Box>

        )} */}
        </Box>
      </Box>
    
  );
};

export default AbsenceDashboard;